import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
// La racine d'API du portail, avec son garde-fou : elle REFUSE de deviner une
// valeur hors développement, pour qu'un bundle de préprod ne parle jamais à la
// production.
import { API_URL } from '../lib/api';

/**
 * Suivi d'une demande par son LIEN, sans compte.
 *
 * C'est la page vers laquelle mènent les e-mails du support. Elle vit dans le
 * portail et non dans un produit, pour deux raisons : la demande peut venir de
 * n'importe lequel des trois, et son destinataire n'a pas forcément de compte —
 * l'envoyer se connecter à une application serait lui fermer la porte au
 * moment où il attend une réponse.
 *
 * ── Le jeton est dans le FRAGMENT ─────────────────────────────────────────
 *
 * `/support/SUP-2026-0042#t=…`, jamais `?t=`. Un fragment n'est pas transmis
 * au serveur : le jeton n'apparaît donc dans aucun journal d'accès, aucun
 * `Referer`, aucun historique de proxy. C'est la seule partie d'une URL à
 * laquelle on peut confier un secret — et celui-ci en est un, puisqu'il ouvre
 * la demande à qui le détient.
 *
 * Il est reposé en EN-TÊTE sur chaque appel, jamais réinjecté dans une URL.
 */



const LIBELLES: Record<string, string> = {
  OUVERT: 'Reçue',
  EN_COURS: 'En cours de traitement',
  EN_ATTENTE_CLIENT: 'En attente de votre réponse',
  RESOLU: 'Résolue',
  CLOS: 'Close',
};

interface Message {
  id: string;
  author: 'CLIENT' | 'AGENT';
  body: string;
  created_at: string;
  attachments?: { id: string; filename: string }[];
}

interface Demande {
  public_ref: string;
  subject: string;
  status: string;
  messages: Message[];
}

/** Jeton de suivi : fragment d'abord, query en repli pour un lien recopié. */
function lireJeton(): string | null {
  if (typeof window === 'undefined') return null;
  const fragment = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('t');
  if (fragment) return fragment;
  return new URLSearchParams(window.location.search).get('token');
}

export function SupportSuiviPage() {
  const { ref } = useParams<{ ref: string }>();
  const jeton = useMemo(() => lireJeton(), []);
  const [demande, setDemande] = useState<Demande | null>(null);
  const [erreur, setErreur] = useState('');
  const [reponse, setReponse] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const entetes = useMemo(
    () => (jeton ? { 'x-support-token': jeton } : undefined),
    [jeton],
  );

  useEffect(() => {
    if (!ref) return;
    let vivant = true;
    fetch(`${API_URL}/support/tickets/${encodeURIComponent(ref)}`, { headers: entetes })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Demande introuvable.'))))
      .then((d) => vivant && setDemande(d))
      .catch((e) => vivant && setErreur(e?.message ?? 'Demande introuvable.'));
    return () => {
      vivant = false;
    };
  }, [ref, entetes]);

  const repondre = async (evenement: React.FormEvent) => {
    evenement.preventDefault();
    if (!reponse.trim() || !ref) return;
    setEnvoi(true);
    setErreur('');
    try {
      const forme = new FormData();
      forme.set('body', reponse.trim());
      const r = await fetch(`${API_URL}/support/tickets/${encodeURIComponent(ref)}/messages`, {
        method: 'POST',
        headers: entetes,
        body: forme,
      });
      if (!r.ok) throw new Error('Envoi impossible.');
      setDemande(await r.json());
      setReponse('');
    } catch (e) {
      setErreur((e as Error)?.message ?? 'Envoi impossible.');
    } finally {
      setEnvoi(false);
    }
  };

  if (!jeton) {
    return (
      <div className="support-panel">
        <h1>Lien incomplet</h1>
        <p>
          Ce lien ne porte pas son jeton de suivi. Ouvrez-le tel qu'il figure dans
          notre e-mail, sans le recopier à la main.
        </p>
      </div>
    );
  }

  if (erreur && !demande) {
    return (
      <div className="support-panel">
        <h1>Demande introuvable</h1>
        <p className="support-panel__erreur" role="alert">{erreur}</p>
      </div>
    );
  }

  if (!demande) return <p role="status">Chargement…</p>;

  return (
    <div className="support-panel">
      <h1>{demande.subject}</h1>
      <p className="support-panel__meta">
        <span className="support-panel__ref">{demande.public_ref}</span>
        {' · '}
        {LIBELLES[demande.status] ?? demande.status}
      </p>

      {erreur && <p className="support-panel__erreur" role="alert">{erreur}</p>}

      <ol className="support-panel__messages">
        {demande.messages.map((message) => (
          <li
            key={message.id}
            className={
              message.author === 'AGENT'
                ? 'support-panel__message support-panel__message--agent'
                : 'support-panel__message'
            }
          >
            <p className="support-panel__auteur">
              {message.author === 'AGENT' ? 'Support SEmply' : 'Vous'}
              {' · '}
              {new Date(message.created_at).toLocaleString('fr-FR')}
            </p>
            <p className="support-panel__corps">{message.body}</p>
            {(message.attachments ?? []).length > 0 && (
              <ul className="support-panel__pj">
                {message.attachments!.map((pj) => (
                  <li key={pj.id}>{pj.filename}</li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ol>

      {demande.status === 'CLOS' ? (
        <p className="support-panel__clos">
          Cette demande est close. Écrivez-nous depuis votre application en citant{' '}
          <strong>{demande.public_ref}</strong> si le sujet revient.
        </p>
      ) : (
        <form className="support-panel__form" onSubmit={repondre}>
          <label htmlFor="reponse">Votre réponse</label>
          <textarea
            id="reponse"
            rows={5}
            value={reponse}
            onChange={(e) => setReponse(e.target.value)}
            disabled={envoi}
          />
          <button type="submit" disabled={envoi || !reponse.trim()}>
            {envoi ? 'Envoi…' : 'Répondre'}
          </button>
        </form>
      )}
    </div>
  );
}
